import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  fetchAdminCatalogue,
  adminUpdateProduct,
  fetchAdminProductPricing,
  adminUpdateProductPricing,
} from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Tag, Package, Globe, Copy, ExternalLink } from "lucide-react";
import BundleContentsEditor from "@/components/BundleContentsEditor";

/**
 * Admin edit sheet for a single product.
 *
 * Top: editable Product fields (title, MSRP, weight, inventory, rank,
 * excerpt, blurb, hero image url, active, slug).
 *
 * Bottom: role-pricing matrix.  One row per canonical role
 * (Customer / Apprentice / Ambassador / Spa / Professional / Subscriber /
 * Dist / Administrator) with MSRP, Role Price, Sale Price + Sale Start /
 * Sale End.  Each row saves independently via PATCH
 * `/postcue/admin/product/{id}/pricing` so a partial save can't
 * lose unrelated edits.
 */
export default function CatalogueEditSheet({
  open,
  onOpenChange,
  productRecordId,
  callerUserRecordId,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState({});
  const [pricingRows, setPricingRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingRoleIdx, setSavingRoleIdx] = useState(null);
  const [showBundle, setShowBundle] = useState(false);

  const load = useCallback(async () => {
    if (!productRecordId || !callerUserRecordId) return;
    setLoading(true);
    try {
      // Pull the product row from the admin catalogue list to avoid a
      // second endpoint just for one product.
      const [list, pricing] = await Promise.all([
        fetchAdminCatalogue({
          callerUserRecordId,
          includeInactive: 1,
        }),
        fetchAdminProductPricing({
          callerUserRecordId,
          productRecordId,
        }),
      ]);
      const p = (list?.products || []).find((x) => x.id === productRecordId);
      setProduct(p || null);
      setForm({
        post_title: p?.name || "",
        price: p?.price ?? 0,
        weight: p?.weight ?? 0,
        inventory: p?.inventory ?? 0,
        topic_rank: p?.rank ?? 0,
        active: p?.active ?? 0,
        excerpt: p?.excerpt || "",
        blurb: p?.blurb || "",
        mi_image_url: p?.image_url || "",
        unique_post_title: p?.slug || "",
      });
      setPricingRows(pricing?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [productRecordId, callerUserRecordId]);

  useEffect(() => {
    if (open && productRecordId) load();
    if (!open) {
      setProduct(null);
      setForm({});
      setPricingRows([]);
      setShowBundle(false);
    }
  }, [open, productRecordId, load]);

  function patchField(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function saveProduct() {
    if (!productRecordId) return;
    setSaving(true);
    try {
      const fields = {
        post_title: String(form.post_title || "").trim(),
        price: Number(form.price || 0),
        weight: Number(form.weight || 0),
        inventory: Number(form.inventory || 0),
        topic_rank: Number(form.topic_rank || 0),
        active: form.active ? 1 : 0,
        excerpt: String(form.excerpt || ""),
        blurb: String(form.blurb || ""),
        mi_image_url: String(form.mi_image_url || "").trim(),
        unique_post_title: String(form.unique_post_title || "").trim(),
      };
      await adminUpdateProduct({
        callerUserRecordId,
        productRecordId,
        fields,
      });
      toast.success("Product saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function savePricingRow(idx) {
    const r = pricingRows[idx];
    if (!r) return;
    setSavingRoleIdx(idx);
    try {
      const res = await adminUpdateProductPricing({
        callerUserRecordId,
        productRecordId,
        role: r.role,
        pricingRecordId: r.pricing_record_id,
        msrp: Number(r.msrp || 0),
        rolePrice: Number(r.role_price || 0),
        salePrice: Number(r.sale_price || 0),
        saleStart: r.sale_start || null,
        saleEnd: r.sale_end || null,
        visible: !!r.visible,
      });
      // If we just created a new row, capture its id for future saves
      if (!r.pricing_record_id && res?.record?.id) {
        setPricingRows((cur) =>
          cur.map((x, i) =>
            i === idx ? { ...x, pricing_record_id: res.record.id } : x
          )
        );
      }
      toast.success(`${r.role} price saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pricing save failed");
    } finally {
      setSavingRoleIdx(null);
    }
  }

  function patchPricing(idx, key, val) {
    setPricingRows((cur) =>
      cur.map((x, i) => (i === idx ? { ...x, [key]: val } : x))
    );
  }

  const hasSaleAnywhere = useMemo(
    () =>
      pricingRows.some(
        (r) => Number(r.sale_price || 0) > 0 || r.sale_start || r.sale_end
      ),
    [pricingRows]
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl flex flex-col p-0"
      >
        <SheetHeader className="border-b border-zinc-200 px-5 py-4 shrink-0">
          <SheetTitle className="text-base font-bold flex items-center gap-2">
            <Package size={16} className="text-violet-700" />
            {product?.name || "Edit product"}
          </SheetTitle>
          {product?.slug && (
            <div className="mt-2 flex items-center gap-2 rounded-xl bg-zinc-50 border border-zinc-200 px-3 py-1.5" data-testid="catalogue-edit-live-url">
              <Globe size={13} className="text-emerald-600 shrink-0" />
              <a
                href={`https://orevital.com/product/${product.slug}`}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate font-mono text-[11px] text-zinc-700 hover:text-violet-700 hover:underline"
                data-testid="catalogue-edit-live-url-link"
              >
                orevital.com/product/{product.slug}
              </a>
              <button
                type="button"
                onClick={() => { navigator.clipboard?.writeText(`https://orevital.com/product/${product.slug}`); toast.success("Product link copied"); }}
                className="h-7 w-7 rounded-lg hover:bg-white text-zinc-500 hover:text-zinc-800 inline-flex items-center justify-center"
                title="Copy link"
                data-testid="catalogue-edit-copy-url"
              >
                <Copy size={13} />
              </button>
              <a
                href={`https://orevital.com/product/${product.slug}`}
                target="_blank"
                rel="noreferrer"
                className="h-7 w-7 rounded-lg hover:bg-white text-zinc-500 hover:text-zinc-800 inline-flex items-center justify-center"
                title="Open live product page"
                data-testid="catalogue-edit-open-url"
              >
                <ExternalLink size={13} />
              </a>
            </div>
          )}
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="animate-spin text-zinc-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
            {/* Hero + core fields */}
            <section className="grid sm:grid-cols-[120px_1fr] gap-4">
              <div className="w-30 h-30 rounded-xl bg-zinc-50 border border-zinc-200 flex items-center justify-center overflow-hidden">
                {form.mi_image_url ? (
                  <img
                    src={form.mi_image_url}
                    alt=""
                    className="w-full h-full object-contain"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : (
                  <Package size={32} className="text-zinc-300" />
                )}
              </div>
              <div className="space-y-2.5">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Product name
                  </Label>
                  <Input
                    value={form.post_title || ""}
                    onChange={(e) => patchField("post_title", e.target.value)}
                    className="h-10 mt-1"
                    data-testid="catalogue-edit-name"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Hero image URL
                  </Label>
                  <Input
                    value={form.mi_image_url || ""}
                    onChange={(e) =>
                      patchField("mi_image_url", e.target.value)
                    }
                    className="h-10 mt-1"
                    placeholder="https://uploads.orevital.com/…"
                  />
                </div>
                <div className="flex items-center justify-between gap-3 pt-1">
                  <Label className="text-sm font-medium text-zinc-700">
                    Active
                  </Label>
                  <Switch
                    checked={!!form.active}
                    onCheckedChange={(v) => patchField("active", v ? 1 : 0)}
                    data-testid="catalogue-edit-active"
                  />
                </div>
              </div>
            </section>

            {/* Numeric fields */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <NumField
                label="MSRP ($)"
                value={form.price}
                onChange={(v) => patchField("price", v)}
                testId="catalogue-edit-msrp"
              />
              <NumField
                label="Weight (oz)"
                value={form.weight}
                onChange={(v) => patchField("weight", v)}
              />
              <NumField
                label="Inventory"
                value={form.inventory}
                onChange={(v) => patchField("inventory", v)}
              />
              <NumField
                label="Rank"
                value={form.topic_rank}
                onChange={(v) => patchField("topic_rank", v)}
              />
            </section>

            <section>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                Slug (URL)
              </Label>
              <Input
                value={form.unique_post_title || ""}
                onChange={(e) =>
                  patchField("unique_post_title", e.target.value)
                }
                className="h-10 mt-1 font-mono text-xs"
                placeholder="my-product-slug"
              />
            </section>

            <section className="space-y-2.5">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Blurb (tagline)
                </Label>
                <Input
                  value={form.blurb || ""}
                  onChange={(e) => patchField("blurb", e.target.value)}
                  className="h-10 mt-1"
                  placeholder="One-line tagline shown under the title"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
                  Short excerpt
                </Label>
                <textarea
                  value={form.excerpt || ""}
                  onChange={(e) => patchField("excerpt", e.target.value)}
                  rows={2}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-zinc-200 text-sm bg-white"
                />
              </div>
            </section>

            <Button
              onClick={saveProduct}
              disabled={saving}
              className="w-full h-11 bg-violet-600 hover:bg-violet-700"
              data-testid="catalogue-edit-save-product"
            >
              {saving ? (
                <Loader2 className="animate-spin" size={16} />
              ) : (
                <>
                  <Save size={15} className="mr-2" />
                  Save product
                </>
              )}
            </Button>

            {(product?.is_bundle || showBundle) ? (
              <BundleContentsEditor
                productRecordId={productRecordId}
                callerUserRecordId={callerUserRecordId}
                price={form.price}
                onSaved={(res) => { if (res?.price != null) patchField("price", res.price); patchField("blurb", res?.blurb || form.blurb); onSaved?.(); }}
              />
            ) : (
              <button
                type="button"
                onClick={() => setShowBundle(true)}
                className="text-xs font-medium text-violet-700 hover:underline text-left"
                data-testid="catalogue-edit-make-bundle"
              >
                + Turn this product into a bundle (pick the products it includes)
              </button>
            )}

            {/* Role pricing matrix */}
            <section className="pt-2 border-t border-zinc-200">
              <div className="flex items-center gap-2 mb-3">
                <Tag size={15} className="text-emerald-600" />
                <h3 className="text-sm font-bold uppercase tracking-widest text-emerald-700">
                  Role pricing
                </h3>
                {hasSaleAnywhere && (
                  <span className="text-[10px] font-semibold text-rose-600 bg-rose-50 rounded-full px-2 py-0.5">
                    Sale active
                  </span>
                )}
              </div>
              <ul className="space-y-2.5">
                {pricingRows.map((r, idx) => (
                  <li
                    key={r.role || idx}
                    className={`rounded-xl border bg-white p-3 ${
                      r.visible ? "border-zinc-200" : "border-zinc-200 opacity-60"
                    }`}
                    data-testid={`catalogue-edit-pricing-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="text-sm font-bold text-zinc-900 capitalize flex-1">
                        {(r.role || "").trim() || "—"}
                      </div>
                      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-zinc-500">
                        Visible
                        <Switch
                          checked={!!r.visible}
                          onCheckedChange={(v) =>
                            patchPricing(idx, "visible", v)
                          }
                          data-testid={`catalogue-edit-pricing-visible-${idx}`}
                        />
                      </label>
                      <Button
                        onClick={() => savePricingRow(idx)}
                        disabled={savingRoleIdx === idx}
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        data-testid={`catalogue-edit-pricing-save-${idx}`}
                      >
                        {savingRoleIdx === idx ? (
                          <Loader2 className="animate-spin" size={12} />
                        ) : (
                          "Save"
                        )}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <NumField
                        label="MSRP ($)"
                        value={r.msrp}
                        onChange={(v) => patchPricing(idx, "msrp", v)}
                      />
                      <NumField
                        label="Role Price ($)"
                        value={r.role_price}
                        onChange={(v) => patchPricing(idx, "role_price", v)}
                      />
                      <NumField
                        label="Sale Price ($)"
                        value={r.sale_price}
                        onChange={(v) => patchPricing(idx, "sale_price", v)}
                        accent={Number(r.sale_price || 0) > 0}
                      />
                      <DateField
                        label="Sale Start"
                        value={r.sale_start}
                        onChange={(v) => patchPricing(idx, "sale_start", v)}
                      />
                      <DateField
                        label="Sale End"
                        value={r.sale_end}
                        onChange={(v) => patchPricing(idx, "sale_end", v)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-zinc-400 mt-3">
                Each row writes to the Airtable <b>Pricing</b> table independently.
                Leaving a row at 0 / blank dates means no sale.
              </p>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function NumField({ label, value, onChange, accent = false, testId }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </Label>
      <Input
        type="number"
        step="0.01"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        className={`h-9 mt-1 text-sm ${
          accent ? "border-rose-300 text-rose-700 font-semibold" : ""
        }`}
        data-testid={testId}
      />
    </div>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
        {label}
      </Label>
      <Input
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 mt-1 text-sm"
      />
    </div>
  );
}
